import React from 'react';

import IconButton from '@atlaskit/button/icon/button';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import type { TooltipProps } from '@atlaskit/tooltip/types';

import { SearchIcon } from '../icons/search-icon';
import { useTheme } from '../theme';
import type { SearchCSS } from '../theme';
import { ArrowIconWrapper } from './arrow-icon-wrapper';
import { EnlargedDesktopSearchIconWrapper } from './enlarged-desktop-search-icon-wrapper';
import { EnlargedSearchInputField } from './enlarged-search-input-field';
import { HiddenTooltip } from './hidden-tooltip';
import { type Props as SearchInputProps } from './search-input';
import { EnlargedSearchInputOverlay } from './search-input-enlarged-overlay';
import { MobileSearchIconWrapper, SearchInputContainer } from './search-input.styled';

interface InternalProps {
	forwardRef?: React.Ref<HTMLInputElement>;
	theme: SearchCSS;
}

const MAX_INPUT_LENGTH = 500;

export const EnlargedSearchInput: ({
	onInput,
	onClick,
	onEnter,
	isExpanded,
	placeholder,
	forwardRef,
	value,
	theme,
	tooltipContent,
	onBack,
	showClearButton,
	onClear,
	allowChangeExpand,
	exitSearchLabel,
	clearSearchLabel,
	shouldFillContainer,
	onSelect,
	ariaLabelContent,
}: SearchInputProps & InternalProps) => React.JSX.Element = ({
	onInput,
	onClick,
	onEnter,
	isExpanded,
	placeholder,
	forwardRef,
	value,
	theme,
	tooltipContent,
	onBack,
	showClearButton,
	onClear,
	allowChangeExpand,
	exitSearchLabel,
	clearSearchLabel,
	shouldFillContainer,
	onSelect,
	ariaLabelContent,
}: SearchInputProps & InternalProps): React.JSX.Element => {
	const handleInput = (e: React.FormEvent<HTMLInputElement>) => {
		if (onInput) {
			onInput(e.currentTarget.value);
		}
	};

	const handleClick = (e: React.MouseEvent<HTMLElement> | undefined) => {
		if (onClick) {
			onClick(e);
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.nativeEvent?.isComposing || e.keyCode === 229) {
			return;
		}
		if (!e.defaultPrevented && e.key === 'Enter') {
			e.preventDefault();
			onEnter && onEnter(e);
		}
	};

	const handleSelect = (e: React.SyntheticEvent) => {
		if (onSelect) {
			onSelect(e);
		}
	};

	const TooltipComponent = tooltipContent && !isExpanded ? undefined : HiddenTooltip;

	return (
		<Tooltip
			// `HiddenTooltip` hides the popup but still renders it. The top-layer tooltip puts
			// role="tooltip" on its own host, which `HiddenTooltip` can't hide, so pass no content.
			content={
				TooltipComponent && fg('platform-dst-top-layer-tooltip') ? undefined : tooltipContent
			}
			// NOTE: Types in React 18 have changed, `styled(TooltipPrimitive)` is no longer valid
			component={TooltipComponent as TooltipProps['component']}
			position="bottom"
		>
			<SearchInputContainer
				{...theme}
				shouldFillContainer={shouldFillContainer}
				isExpanded={isExpanded}
				role="search"
			>
				<MobileSearchIconWrapper isExpanded={isExpanded}>
					<IconButton
						onClick={handleClick}
						label="Search"
						icon={() => <SearchIcon theme={theme} isExpanded={isExpanded} />}
					/>
				</MobileSearchIconWrapper>
				<EnlargedDesktopSearchIconWrapper {...theme} isExpanded={isExpanded}>
					<SearchIcon
						theme={theme}
						isExpanded={isExpanded}
						primaryColor={token('color.icon.subtle')}
					/>
				</EnlargedDesktopSearchIconWrapper>
				{isExpanded && (
					<ArrowIconWrapper {...theme} onClick={onBack}>
						<ArrowLeftIcon label={exitSearchLabel || 'Exit Search'} color="currentColor" />
					</ArrowIconWrapper>
				)}
				<EnlargedSearchInputField
					data-test-id="search-dialog-input"
					onInput={handleInput}
					onChange={handleInput}
					onKeyDown={handleKeyDown}
					onClick={handleClick}
					onSelect={handleSelect}
					isExpanded={isExpanded}
					placeholder={placeholder}
					ref={forwardRef || undefined}
					value={value}
					maxLength={MAX_INPUT_LENGTH}
					aria-label={ariaLabelContent || placeholder}
					{...theme}
				/>
				{showClearButton && (
					<EnlargedSearchInputOverlay
						theme={theme}
						showClearButton={showClearButton}
						onClear={onClear}
						allowChangeExpand={allowChangeExpand}
						clearSearchLabel={clearSearchLabel}
						isExpanded={isExpanded}
					/>
				)}
			</SearchInputContainer>
		</Tooltip>
	);
};

const EnlargedSearchInputWithTheme: React.ForwardRefExoticComponent<
	SearchInputProps & React.RefAttributes<HTMLInputElement>
> = React.forwardRef<HTMLInputElement, SearchInputProps>((props, ref) => {
	const theme = useTheme();
	return <EnlargedSearchInput {...props} theme={theme} forwardRef={ref} />;
});
EnlargedSearchInputWithTheme.displayName = 'EnlargedSearchInput';
export default EnlargedSearchInputWithTheme;
